/// <amd-module name="Feed/Legacy/Protocols/Components/PackageDependencies" />
import * as React from "react";

import { IVssPageContext } from "VSS/Platform/Context";

import { Link } from "VSSUI/Link";

import { Feed, Package, PackageVersion } from "Feed/Common/Contracts/Feed";
import * as PackageResources from "Feed/Common/Resources";
import { IDependency, IDependencyGroup } from "Feed/Common/Types/IDependency";
import * as Actions from "Feed/Legacy/Actions/Actions";
import { IPackageDependencySelectedPayload } from "Feed/Legacy/Common/ActionPayloads";
import { DependencyHelper } from "Feed/Legacy/Helpers/DependencyHelper";
import { PackageAttribute } from "Feed/Legacy/Protocols/Components/PackageAttribute";

export interface IPackageDependenciesProps {
    pageContext: IVssPageContext;
    feed: Feed;
    packageSummary: Package;
    packageVersion: PackageVersion;
    dependencyHelperCallback?: (dg: IDependencyGroup[]) => void;
    isSmartDependenciesEnabled?: boolean;
}

export interface IPackageDependenciesState {
    dependencyGroups: IDependencyGroup[];
}

export class PackageDependencies extends React.Component<IPackageDependenciesProps, IPackageDependenciesState> {
    private _mounted: boolean;

    constructor(props: IPackageDependenciesProps) {
        super(props);
        let depGroups = null;

        depGroups = this.props.packageVersion
            ? DependencyHelper.groupDependencies(this.props.packageVersion.dependencies)
            : null;

        if (this.props.dependencyHelperCallback) {
            this.props.dependencyHelperCallback(depGroups);
        }

        // Load state with standard list of dependencies first
        this.state = {
            dependencyGroups: depGroups
        } as IPackageDependenciesState;

        this._mounted = false;
    }

    public componentDidMount(): void {
        this._mounted = true;
        let depGroups = null;
        if (this.props.packageVersion.dependencies && this.props.isSmartDependenciesEnabled === true) {
            (async () => {
                const packageDependencies = await DependencyHelper.getDependenciesForPackageVersionAsync(
                    this.props.pageContext,
                    this.props.feed.project ? this.props.feed.project.name : null,
                    this.props.feed.id,
                    this.props.packageSummary.id,
                    this.props.packageVersion.id,
                    this.props.packageSummary.protocolType
                );
                if (this._mounted === true && packageDependencies) {
                    depGroups = DependencyHelper.groupDependencyDetails(
                        this.props.pageContext,
                        packageDependencies,
                        this.props.feed.name,
                        this.props.packageSummary.protocolType
                    );
                    if (this.props.dependencyHelperCallback) {
                        this.props.dependencyHelperCallback(depGroups);
                    }
                    this.setState({
                        dependencyGroups: depGroups
                    });
                }
            })();
        }
    }

    public componentWillUnmount(): void {
        this._mounted = false;
    }

    public render(): JSX.Element {
        return (
            <PackageAttribute title={PackageResources.PackageDependencies_Title} className="package-dependencies">
                {this.getDependencies()}
            </PackageAttribute>
        );
    }

    getDependencies(): JSX.Element[] {
        if (!this.state.dependencyGroups || this.state.dependencyGroups.length < 1) {
            return null;
        }

        return this.state.dependencyGroups.map((dependencyGroup: IDependencyGroup) => {
            const dependencies = dependencyGroup.dependencies;

            if (dependencies && dependencies.length > 0) {
                return (
                    <div key={"DependencyGroupSection_" + dependencyGroup.group}>
                        <div className="package-group-header" key={dependencyGroup.group} role="heading" aria-level={3}>
                            {dependencyGroup.group}
                        </div>
                        <ul>
                            {dependencies.map(dependency => {
                                dependency.name = dependency.name || "";
                                dependency.versionRange = dependency.versionRange || "";

                                if (dependency.packageId != null) {
                                    return this.getLink(dependency);
                                } else {
                                    return (
                                        <li
                                            className="package-dependency"
                                            key={dependency.name + dependency.versionRange}
                                        >
                                            {dependency.name + " " + dependency.versionRange}
                                        </li>
                                    );
                                }
                            })}
                        </ul>
                        <div className="package-group-spacer" />
                    </div>
                );
            }
        });
    }

    getLink(dependency: IDependency): JSX.Element {
        return (
            <li className="package-dependency" key={dependency.name + dependency.versionRange}>
                <Link
                    onClick={event => {
                        // allow browser's default behavior of Ctrl+Click to open dependency in new tab
                        if (event.ctrlKey) {
                            return;
                        }
                        event.preventDefault();
                        Actions.PackageDependencySelected.invoke({
                            dependency,
                            originPackage: this.props.packageSummary
                        } as IPackageDependencySelectedPayload);
                    }}
                    href={dependency.link}
                >
                    {dependency.name}
                </Link>
                {" " + dependency.versionRange}
            </li>
        );
    }
}
